import { useCallback, useEffect, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Link,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useTranslation } from "i18n-fe";
import AppShell from "./AppShell";
import PageBreadcrumbs from "./PageBreadcrumbs";
import { useScoreContext } from "../context/ScoreContext";
import { syncEngine } from "../local/sync";
import {
  buildOutboxSheetRows,
  OUTBOX_SHEET_STATE,
} from "../local/outboxSheets";
import { HOLD_REASONS } from "../local/holdReasons";
import { buildInboxRows, INBOX_FLAG } from "../local/inboxClasses";
import { getCurrentOwnerSub } from "../local/db";
import { classNumberLabel } from "../util/classDisplay";
import { formatVenueTimeOrUnset } from "common-fe/venueTime";
import SendSnapshotDialog from "./SendSnapshotDialog";

// Card #2826 judge round 1, finding 1 -- a runtime-built i18n key
// (`queuePage.state.${row.state}`) is forbidden even though every value
// row.state can hold is one of OUTBOX_SHEET_STATE's own literals: an i18n
// key extractor can only see literal strings passed to t(), so this static
// map is what makes every key show up as a real, extractable literal,
// covering every OUTBOX_SHEET_STATE value.
const STATE_LABEL_KEYS = {
  [OUTBOX_SHEET_STATE.HELD]: "queuePage.state.held",
  [OUTBOX_SHEET_STATE.WAITING]: "queuePage.state.waiting",
  [OUTBOX_SHEET_STATE.NEEDS_ATTENTION]: "queuePage.state.needsAttention",
  [OUTBOX_SHEET_STATE.SENDING]: "queuePage.state.sending",
  [OUTBOX_SHEET_STATE.QUEUED]: "queuePage.state.queued",
  // Card #3334 -- same static-literal rule as every other entry here.
  [OUTBOX_SHEET_STATE.RESENDING]: "queuePage.state.resending",
  [OUTBOX_SHEET_STATE.SHIPPED]: "queuePage.state.shipped",
  [OUTBOX_SHEET_STATE.IN_REVIEW]: "queuePage.state.inReview",
  // #3088 -- same static-literal rule as every other entry here.
  [OUTBOX_SHEET_STATE.RIDE_IN_REVIEW]: "queuePage.state.rideInReview",
  [OUTBOX_SHEET_STATE.DRAFT]: "queuePage.state.draft",
  // Card #3014 -- same static-literal rule as every other entry here.
  [OUTBOX_SHEET_STATE.DISCARDED]: "queuePage.state.discarded",
  [OUTBOX_SHEET_STATE.QUARANTINED]: "queuePage.state.quarantined",
  // Card #3012 -- same static-literal rule as every other entry here.
  [OUTBOX_SHEET_STATE.RESOLVED]: "queuePage.state.resolved",
};

// Finding 2 -- outboxSheets.js now hands back raw ACTIONS.* ids
// (row.actionChoices, e.g. ["quarantine", "discard"]), never pre-translated
// English text; this is the one static, literal-keyed map from id to
// locale key, joined with the locale's own "or" text.
const ACTION_LABEL_KEYS = {
  retry: "queuePage.action.retry",
  discard: "queuePage.action.discard",
  quarantine: "queuePage.action.quarantine",
};

function actionText(t, actionChoices) {
  if (!actionChoices || actionChoices.length === 0) return null;
  return actionChoices
    .map((choice) =>
      ACTION_LABEL_KEYS[choice] ? t(ACTION_LABEL_KEYS[choice]) : choice,
    )
    .join(t("queuePage.action.join"));
}

// Card #3253 -- a waiting row says WHAT it waits for. One static key per
// holdReason (no dynamic i18n keys); an unknown reason falls back to the
// generic "Waiting" label, never to a blank cell.
function stateLabel(t, row) {
  if (
    row.state === OUTBOX_SHEET_STATE.WAITING &&
    row.holdReason === HOLD_REASONS.JUDGE_SIGNATURE
  ) {
    return t("queuePage.state.waitingJudgeSignature");
  }
  return t(
    STATE_LABEL_KEYS[row.state] ?? STATE_LABEL_KEYS[OUTBOX_SHEET_STATE.QUEUED],
  );
}

function problemCellText(t, row) {
  // Card #3249 -- a signature-held row is not a problem, but the scribe needs
  // to know who to ask. Plain words, never "failed". Static key per reason.
  if (
    row.state === OUTBOX_SHEET_STATE.WAITING &&
    row.holdReason === HOLD_REASONS.JUDGE_SIGNATURE
  ) {
    return t("queuePage.waitingJudgeSignatureNote");
  }
  if (!row.problem) return "";
  const action = actionText(t, row.actionChoices);
  return action ? `${row.problem} (${action})` : row.problem;
}

// Card #2826 (docs/score/scribe-resilience.md gap G12 part A) -- every score
// sheet this station has ever touched for the active show, shipped and
// unshipped alike, in one READ-ONLY table (no retry/discard here, those stay
// on SyncStatus.jsx; AC4/"send snapshot now" moved to a split card). AC2:
// this page paints entirely off buildOutboxSheetRows (../local/outboxSheets),
// which reads only sync.js's in-memory op lists plus IndexedDB caches, so it
// works offline with zero network calls of its own.
function sheetLabel(row) {
  if (row.horseName) {
    return row.riderName
      ? `${row.horseName} / ${row.riderName}`
      : row.horseName;
  }
  return row.riderName ?? row.rideId ?? "";
}

// Card #3276 -- a real react-router link (keyboard reachable, shows as
// clickable) when the target id is present; plain text otherwise, so an
// orphaned row (no classId / rideId) renders without a dead link or a crash.
function MaybeLink({ to, children }) {
  if (!to) return children;
  return (
    <Link component={RouterLink} to={to}>
      {children}
    </Link>
  );
}

const classHref = (classId) => (classId ? `/classes/${classId}` : null);
const rideScoreHref = (rideId) => (rideId ? `/rides/${rideId}/score` : null);

// Static-literal keys, same rule as STATE_LABEL_KEYS above.
const INBOX_FLAG_KEYS = {
  [INBOX_FLAG.PULL_FAILED]: "queuePage.inbox.flag.pullFailed",
};

// Card #3274 -- the Queue page is two collapsible sections. Each header says
// what the section is, how many items it holds, and when it last changed.
const TICK_MS = 30 * 1000;
// An Outbox "Last update" turns red only when something is still waiting to
// send AND the last upload is older than this.
const STALE_UPLOAD_MS = 10 * 60 * 1000;

// Sheets whose op is still in the outbox: not yet acknowledged by the server.
// Shipped / resolved / discarded / quarantined / in-review / draft rows are not
// waiting on the network.
const WAITING_TO_SEND_STATES = new Set([
  OUTBOX_SHEET_STATE.HELD,
  OUTBOX_SHEET_STATE.WAITING,
  OUTBOX_SHEET_STATE.NEEDS_ATTENTION,
  OUTBOX_SHEET_STATE.SENDING,
  OUTBOX_SHEET_STATE.QUEUED,
  OUTBOX_SHEET_STATE.RESENDING,
]);

// A tombstone's shippedAt is when the op was discarded, not an upload.
const NOT_AN_UPLOAD_STATES = new Set([
  OUTBOX_SHEET_STATE.DISCARDED,
  OUTBOX_SHEET_STATE.QUARANTINED,
]);

function toMs(iso) {
  const ms = iso ? new Date(iso).getTime() : NaN;
  return Number.isNaN(ms) ? null : ms;
}

// Newest upload time (ms) across the Outbox rows, or null when none.
function newestUploadMs(rows) {
  let newest = null;
  for (const row of rows) {
    if (NOT_AN_UPLOAD_STATES.has(row.state)) continue;
    const ms = toMs(row.shippedAt);
    if (ms !== null && (newest === null || ms > newest)) newest = ms;
  }
  return newest;
}

// Card #3279 -- when nothing has ever uploaded, how long the oldest
// still-waiting sheet has been waiting is measured from that sheet's own
// persisted queued time (survives a reload; page-open time would not).
// Returns the oldest queuedAt (ms) among waiting rows, or null.
function oldestWaitingQueuedMs(rows) {
  let oldest = null;
  for (const row of rows) {
    if (!WAITING_TO_SEND_STATES.has(row.state)) continue;
    const ms = toMs(row.queuedAt);
    if (ms !== null && (oldest === null || ms < oldest)) oldest = ms;
  }
  return oldest;
}

// Re-renders on an interval so the relative "x ago" text keeps ticking.
function useNow() {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), TICK_MS);
    return () => clearInterval(id);
  }, []);
  return now;
}

// Static literal keys only (no dynamic i18n keys).
function relativeAgo(t, ms, now) {
  const minutes = Math.max(0, Math.floor((now - ms) / 60000));
  if (minutes < 1) return t("queuePage.ago.justNow");
  if (minutes < 60) return t("queuePage.ago.minutes", { count: minutes });
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return t("queuePage.ago.hours", { count: hours });
  return t("queuePage.ago.days", { count: Math.floor(hours / 24) });
}

function SectionHeader({ id, heading, lastUpdate, stale = false }) {
  return (
    <AccordionSummary
      expandIcon={<ExpandMoreIcon />}
      id={`${id}-header`}
      aria-controls={`${id}-content`}
      data-testid={`${id}-header`}
      sx={{ minHeight: 64 }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          columnGap: 2,
          width: "100%",
          pr: 1,
        }}
      >
        <Typography variant="h6" component="span" data-testid={`${id}-heading`}>
          {heading}
        </Typography>
        <Typography
          component="span"
          data-testid={`${id}-last-update`}
          color={stale ? "error" : "text.secondary"}
          sx={{ fontWeight: stale ? "bold" : undefined }}
        >
          {lastUpdate}
        </Typography>
      </Box>
    </AccordionSummary>
  );
}

export default function Queue() {
  const { t } = useTranslation("score");
  const { selectedShow } = useScoreContext();
  const [rows, setRows] = useState([]);
  const [snapshotOpen, setSnapshotOpen] = useState(false);
  const [inbox, setInbox] = useState(null);
  const now = useNow();

  const refresh = useCallback(async () => {
    const [allOps, surfacedOps] = await Promise.all([
      syncEngine.listAllOps(),
      syncEngine.listSurfacedOps(),
    ]);
    setRows(
      await buildOutboxSheetRows({
        allOps,
        surfacedOps,
        showId: selectedShow?.id,
      }),
    );
    // Card #3198 - the Inbox reads IndexedDB only (no network), and repaints
    // on the same sync-engine ticks as the Outbox.
    // An unreadable IndexedDB leaves the Inbox empty; it must never take the
    // Outbox list down with it.
    try {
      setInbox(
        await buildInboxRows({
          showId: selectedShow?.id,
          sub: getCurrentOwnerSub(),
        }),
      );
    } catch {
      setInbox({ rows: [], lastPulledAt: null });
    }
  }, [selectedShow?.id]);

  // syncEngine.subscribe (../local/sync) invokes fn once immediately with the
  // current state and again on every later change -- the same wiring
  // ScoreContext.jsx and useStationSnapshotUpload.js already use -- so this
  // table repaints itself whenever an op ships, fails, or gets queued,
  // without a poll.
  useEffect(() => {
    const unsubscribe = syncEngine.subscribe(() => {
      void refresh();
    });
    return unsubscribe;
  }, [refresh]);

  const inboxCount = inbox ? inbox.rows.length : 0;
  const inboxPulledMs = toMs(inbox?.lastPulledAt);
  const inboxLastUpdate =
    inboxPulledMs === null
      ? t("queuePage.inbox.noDownloads")
      : t("queuePage.lastUpdate", { ago: relativeAgo(t, inboxPulledMs, now) });
  const uploadMs = newestUploadMs(rows);
  const outboxLastUpdate =
    uploadMs === null
      ? t("queuePage.outbox.noUploads")
      : t("queuePage.lastUpdate", { ago: relativeAgo(t, uploadMs, now) });
  const anyWaiting = rows.some((row) => WAITING_TO_SEND_STATES.has(row.state));
  // With an upload on record, staleness is measured from it. With none ever
  // (fresh tablet, bad auth, wrong env), from the oldest waiting sheet's own
  // queued time, so "No uploads yet" still goes red when sheets are stuck.
  const staleSinceMs = uploadMs ?? oldestWaitingQueuedMs(rows);
  const outboxStale =
    anyWaiting && staleSinceMs !== null && now - staleSinceMs > STALE_UPLOAD_MS;

  const crumbs = [{ label: t("queuePage.title") }];

  return (
    <AppShell>
      <PageBreadcrumbs items={crumbs} />
      <Box sx={{ p: 2 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mb: 2,
          }}
        >
          <Typography variant="h5">{t("queuePage.title")}</Typography>
          <Button
            variant="outlined"
            size="small"
            onClick={() => setSnapshotOpen(true)}
          >
            {t("queuePage.sendNow")}
          </Button>
        </Box>
        <Accordion disableGutters sx={{ mb: 2 }}>
          <SectionHeader
            id="queue-inbox"
            heading={t("queuePage.inbox.heading", { count: inboxCount })}
            lastUpdate={inboxLastUpdate}
          />
          <AccordionDetails id="queue-inbox-content">
            {inbox === null ? null : inbox.rows.length === 0 ? (
              <Typography color="text.secondary">
                {t("queuePage.inbox.empty")}
              </Typography>
            ) : (
              <TableContainer component={Paper}>
                <Table size="small" aria-label={t("queuePage.inbox.title")}>
                  <TableHead>
                    <TableRow>
                      <TableCell>
                        {t("queuePage.inbox.columns.class")}
                      </TableCell>
                      <TableCell>{t("queuePage.inbox.columns.name")}</TableCell>
                      <TableCell>
                        {t("queuePage.inbox.columns.rides")}
                      </TableCell>
                      <TableCell>
                        {t("queuePage.inbox.columns.status")}
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {inbox.rows.map((row) => (
                      <TableRow
                        key={row.classId}
                        data-testid={`inbox-row-${row.classId}`}
                      >
                        <TableCell>
                          <MaybeLink to={classHref(row.classId)}>
                            {row.classNumber}
                          </MaybeLink>
                        </TableCell>
                        <TableCell>
                          <MaybeLink to={classHref(row.classId)}>
                            {row.className}
                          </MaybeLink>
                        </TableCell>
                        <TableCell>{row.rideCount}</TableCell>
                        <TableCell
                          sx={{
                            color: row.flags.length ? "error.main" : undefined,
                          }}
                        >
                          {row.flags
                            .map((flag) => t(INBOX_FLAG_KEYS[flag]))
                            .join(", ")}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </AccordionDetails>
        </Accordion>
        <Accordion disableGutters>
          <SectionHeader
            id="queue-outbox"
            heading={t("queuePage.outbox.heading", { count: rows.length })}
            lastUpdate={outboxLastUpdate}
            stale={outboxStale}
          />
          <AccordionDetails id="queue-outbox-content">
            {rows.length === 0 ? (
              <Typography color="text.secondary">
                {t("queuePage.empty")}
              </Typography>
            ) : (
              <TableContainer component={Paper}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>{t("queuePage.columns.sheet")}</TableCell>
                      <TableCell>{t("queuePage.columns.class")}</TableCell>
                      <TableCell>{t("queuePage.columns.judge")}</TableCell>
                      <TableCell>{t("queuePage.columns.state")}</TableCell>
                      <TableCell>{t("queuePage.columns.problem")}</TableCell>
                      <TableCell>{t("queuePage.columns.shippedAt")}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {rows.map((row) => (
                      <TableRow key={row.key}>
                        <TableCell>
                          <MaybeLink to={rideScoreHref(row.rideId)}>
                            {sheetLabel(row)}
                          </MaybeLink>
                        </TableCell>
                        <TableCell>
                          <MaybeLink to={classHref(row.classId)}>
                            {classNumberLabel(row)}
                          </MaybeLink>
                        </TableCell>
                        <TableCell>{row.judgePosition ?? ""}</TableCell>
                        <TableCell>{stateLabel(t, row)}</TableCell>
                        <TableCell>{problemCellText(t, row)}</TableCell>
                        {/* #2097 - shippedAt is the local ack instant (db.js acknowledgedAt, a
                            genuine new Date().toISOString()), NOT an FVD stamp, so it
                            belongs on the show's venueTimezone. */}
                        <TableCell>
                          {formatVenueTimeOrUnset(row.shippedAt, {
                            timeZone: selectedShow?.venueTimezone,
                            unsetText: t("venueClock.unset"),
                          })}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </AccordionDetails>
        </Accordion>
      </Box>
      {snapshotOpen && (
        <SendSnapshotDialog onClose={() => setSnapshotOpen(false)} />
      )}
    </AppShell>
  );
}
